const getDocument = () => (typeof document !== 'undefined' ? document : null);
const getNodeFilter = () =>
  typeof NodeFilter !== 'undefined' ? NodeFilter : null;

export const getNodeFilterConstants = ({
  nodeFilterRef = getNodeFilter(),
} = {}) => ({
  FILTER_ACCEPT: nodeFilterRef?.FILTER_ACCEPT ?? 1,
  FILTER_REJECT: nodeFilterRef?.FILTER_REJECT ?? 2,
  FILTER_SKIP: nodeFilterRef?.FILTER_SKIP ?? 3,
  SHOW_ELEMENT: nodeFilterRef?.SHOW_ELEMENT ?? 1,
});

export const safeCreateElement = (
  tagName = 'div',
  { documentRef = getDocument() } = {}
) => {
  if (!tagName || !documentRef?.createElement) {
    return null;
  }

  return documentRef.createElement(tagName);
};

export const safeCreateDocumentFragment = ({
  documentRef = getDocument(),
} = {}) => {
  if (!documentRef?.createDocumentFragment) {
    return null;
  }

  return documentRef.createDocumentFragment();
};

export const safeCreateTextNode = (
  text = '',
  { documentRef = getDocument() } = {}
) => {
  if (!documentRef?.createTextNode) {
    return null;
  }

  return documentRef.createTextNode(text);
};

export const safeCreateTreeWalker = (
  root,
  {
    documentRef = getDocument(),
    filter = null,
    nodeFilterRef = getNodeFilter(),
    whatToShow = getNodeFilterConstants({ nodeFilterRef }).SHOW_ELEMENT,
  } = {}
) => {
  if (!root || typeof documentRef?.createTreeWalker !== 'function') {
    return null;
  }

  try {
    return documentRef.createTreeWalker(root, whatToShow, filter, false);
  } catch {
    return null;
  }
};

export const safeGetActiveElement = ({ documentRef = getDocument() } = {}) => {
  try {
    return documentRef?.activeElement || null;
  } catch {
    return null;
  }
};

export const safeGetElementById = (
  id,
  { documentRef = getDocument() } = {}
) => {
  if (!id || typeof documentRef?.getElementById !== 'function') {
    return null;
  }

  try {
    return documentRef.getElementById(id);
  } catch {
    return null;
  }
};

export const safeElementsFromPoint = (
  x = 0,
  y = 0,
  { documentRef = getDocument() } = {}
) => {
  if (typeof documentRef?.elementsFromPoint !== 'function') {
    return [];
  }

  try {
    return Array.from(documentRef.elementsFromPoint(x, y) || []);
  } catch {
    return [];
  }
};

export const safeQuerySelector = (selector, root = getDocument()) => {
  if (!selector || !root?.querySelector) {
    return null;
  }

  try {
    return root.querySelector(selector);
  } catch {
    return null;
  }
};

export const safeQuerySelectorAll = (selector, root = getDocument()) => {
  if (!selector || !root?.querySelectorAll) {
    return [];
  }

  try {
    return Array.from(root.querySelectorAll(selector));
  } catch {
    return [];
  }
};

export const safeGetDocumentBody = ({ documentRef = getDocument() } = {}) => {
  try {
    return documentRef?.body || null;
  } catch {
    return null;
  }
};

export const safeSetDocumentElementAttribute = (
  attributeName,
  value,
  { documentRef = getDocument() } = {}
) => {
  try {
    if (
      !attributeName ||
      typeof documentRef?.documentElement?.setAttribute !== 'function'
    ) {
      return false;
    }

    documentRef.documentElement.setAttribute(attributeName, value);
    return true;
  } catch {
    return false;
  }
};

export const safeIsBodyChild = (node, { documentRef = getDocument() } = {}) => {
  const body = safeGetDocumentBody({ documentRef });
  return !!node && !!body && node.parentNode === body;
};

export const safeAddBodyClass = (
  className,
  { documentRef = getDocument() } = {}
) => {
  const body = safeGetDocumentBody({ documentRef });
  if (!className || !body?.classList?.add) {
    return false;
  }

  try {
    body.classList.add(className);
    return true;
  } catch {
    return false;
  }
};

export const safeRemoveBodyClass = (
  className,
  { documentRef = getDocument() } = {}
) => {
  const body = safeGetDocumentBody({ documentRef });
  if (!className || !body?.classList?.remove) {
    return false;
  }

  try {
    body.classList.remove(className);
    return true;
  } catch {
    return false;
  }
};

export const safeSetBodyCursor = (
  value = '',
  { documentRef = getDocument() } = {}
) => {
  const body = safeGetDocumentBody({ documentRef });
  if (!body?.style) {
    return false;
  }

  body.style.cursor = value;
  return true;
};

export const safeAppendToHead = (
  node,
  { documentRef = getDocument() } = {}
) => {
  const head = documentRef?.head;
  if (!node || !head?.appendChild) {
    return false;
  }

  head.appendChild(node);
  return true;
};

export const safeRemoveFromHead = (
  node,
  { documentRef = getDocument() } = {}
) => {
  const head = documentRef?.head;
  if (!node || !head?.removeChild) {
    return false;
  }

  try {
    head.removeChild(node);
    return true;
  } catch {
    return false;
  }
};

export const safeAppendToBody = (
  node,
  { documentRef = getDocument() } = {}
) => {
  const body = safeGetDocumentBody({ documentRef });
  if (!node || !body?.appendChild) {
    return false;
  }

  body.appendChild(node);
  return true;
};

export const safeRemoveFromBody = (
  node,
  { documentRef = getDocument() } = {}
) => {
  const body = safeGetDocumentBody({ documentRef });
  if (!node || !body?.removeChild) {
    return false;
  }

  try {
    body.removeChild(node);
    return true;
  } catch {
    return false;
  }
};
